// [목적] 봇 답변을 한 글자씩 그려내는 타이프라이터 훅 (+ 클릭 시 즉시 완료)
// [이유] 2026-09-20 chat-typewriter(사용자 요청): 답변이 통째로 "툭" 나타나 대화가 기계적으로 느껴졌다.
//        현재 POST /rag/:id/turn 은 완성된 JSON 을 한 번에 내려주므로(= 토큰 스트리밍 아님), 이미 받은
//        텍스트를 클라이언트에서 점진 렌더해 "말하는 리듬"만 재현한다. 실제 대기시간이 줄지는 않는다 —
//        체감 지연까지 줄이려면 /turn 을 SSE 로 재설계해야 하는데 그건 슬롯추출·clarify 후보 생성이 엮인
//        구조화 출력 파이프라인을 쪼개는 별도 작업이다(이 훅은 그 작업과 충돌하지 않는다 — 스트리밍이
//        도입되면 text 가 점진적으로 커지고, 아래 "이미 출력한 길이부터 이어쓰기" 로직이 그대로 동작한다).
// [사이드 이팩트] setTimeout 기반 — 언마운트/텍스트 변경 시 반드시 정리한다(누수·유령 타이머 방지).
//                 prefers-reduced-motion: reduce 이면 애니메이션 없이 전체 텍스트를 즉시 반환한다(WCAG 2.3.3).
// [수정 시 고려사항] 지연값은 "읽는 리듬" 기준으로 잡혀 있다 — 문장부호에서 쉬지 않으면 속도가 일정해
//                   오히려 기계적으로 보인다. 값 변경 시 반드시 실제 한국어 답변으로 체감 확인할 것.
//                   글자 단위 setState 이므로 호출부(버블)는 가볍게 유지해야 한다 — 무거운 트리 안에서
//                   쓰면 매 글자마다 리렌더 비용이 발생한다.

import { useEffect, useRef, useState } from 'react';

/**
 * [목적] 글자별 다음 지연(ms) — 문장부호/줄바꿈에서 쉬어 사람이 말하는 리듬을 만든다
 * [이유] 균일 간격은 "타이핑"이 아니라 "스크롤"처럼 보인다.
 * [수정 시 고려사항] BASE 를 20 이하로 내리면 연출이 거의 안 보이고, 50 이상이면 답답하다.
 *                   (2026-09-20 사용자 확인: 34ms 채택 — 기존 시안 18ms 는 너무 빨랐다)
 */
const DELAY = {
  base: 34,
  comma: 110,
  sentence: 260,
  newline: 300,
} as const;

const SENTENCE_END = '.!?。';
const COMMA_LIKE = ',·、';

function delayFor(ch: string): number {
  if (ch === '\n') return DELAY.newline;
  if (SENTENCE_END.includes(ch)) return DELAY.sentence;
  if (COMMA_LIKE.includes(ch)) return DELAY.comma;
  return DELAY.base;
}

/**
 * [목적] 모션 축소 선호 여부 — OS/브라우저 설정 존중
 * [이유] WCAG 2.3.3 Animation from Interactions. 전정기관 장애 사용자에게 점진 렌더는 피로를 준다.
 * [사이드 이팩트] matchMedia 미지원 환경(구형 jsdom 등)에서는 false 로 폴백해 기존 동작을 유지한다.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface UseTypewriterResult {
  /** 지금까지 출력된 부분 문자열 */
  displayed: string;
  /** 아직 출력 중인지 — 호출부가 커서 표시 여부를 결정 */
  isTyping: boolean;
  /** 남은 글자를 건너뛰고 즉시 전체 표시 */
  skip: () => void;
}

/**
 * [목적] text 를 한 글자씩 노출
 * [이유] 파일 헤더 주석 참조.
 * [사이드 이팩트] enabled=false 면 타이머를 만들지 않고 전체 텍스트를 즉시 반환한다 —
 *                 과거 메시지(이미 읽은 대화)가 스크롤/리렌더마다 다시 타이핑되는 것을 호출부가 막을 수 있다.
 * [수정 시 고려사항] text 가 "늘어나는" 경우(향후 서버 스트리밍)에는 이미 출력한 접두사를 유지한 채
 *                   이어서 쓴다 — 처음부터 다시 타이핑하지 않는다. text 가 완전히 바뀌면 처음부터 시작.
 */
export function useTypewriter(text: string, enabled = true): UseTypewriterResult {
  const [count, setCount] = useState(() => (enabled && !prefersReducedMotion() ? 0 : text.length));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTextRef = useRef(text);

  useEffect(() => {
    // text 가 기존 출력의 연장(접두사 일치)이 아니면 처음부터 다시 그린다.
    if (!text.startsWith(prevTextRef.current)) {
      setCount(0);
    }
    prevTextRef.current = text;
  }, [text]);

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) {
      setCount(text.length);
      return;
    }

    let cancelled = false;
    // [주의] 진행 인덱스는 setCount 업데이터 안이 아니라 이 로컬 변수로 관리한다.
    // 업데이터 함수 안에서 setTimeout 을 걸면 React StrictMode 가 업데이터를 2회 호출하면서
    // 타이머가 중복 예약되어 글자가 들쪽날쪽 빨라진다(dev 전용 재현 버그).
    let i = 0;

    const tick = (): void => {
      if (cancelled || i >= text.length) return;
      const ch = text[i] ?? '';
      i += 1;
      setCount(i);
      timerRef.current = setTimeout(tick, delayFor(ch));
    };

    // 첫 글자는 즉시 — 버블이 잠깐 빈 채로 떠 있는 것을 막는다.
    timerRef.current = setTimeout(tick, 0);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [text, enabled]);

  const skip = (): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setCount(text.length);
  };

  return {
    displayed: text.slice(0, count),
    isTyping: count < text.length,
    skip,
  };
}
